import React, { useCallback, useMemo } from 'react';
import {
  Box,
  Button,
  FormControl,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Typography,
  type SelectChangeEvent,
} from '@mui/material';
import { ChevronLeft as ChevronLeftIcon, ChevronRight as ChevronRightIcon } from '@mui/icons-material';
import { colors, typography } from '../styles/theme';

export type PaginationBarProps = {
  /** 1-indexed current page */
  page: number;
  /** total pages (>= 1) */
  totalPages: number;
  onChange: (nextPage: number) => void;
  /** Hide when totalPages <= 1 */
  hideIfSinglePage?: boolean;
  /** When set together with `onRowsPerPageChange`, shows “Rows per page” on the left. */
  rowsPerPage?: number;
  rowsPerPageOptions?: number[];
  onRowsPerPageChange?: (rows: number) => void;
};

const DEFAULT_ROWS_OPTIONS = [5, 10, 20, 50];

function pageRange(totalPages: number, currentPage: number): (number | '…')[] {
  if (totalPages <= 1) return [1];
  if (totalPages <= 5) return Array.from({ length: totalPages }, (_, i) => i + 1);

  const pages = new Set<number>([1, totalPages, currentPage]);
  if (currentPage - 1 >= 1) pages.add(currentPage - 1);
  if (currentPage + 1 <= totalPages) pages.add(currentPage + 1);

  const sorted = Array.from(pages).sort((a, b) => a - b);
  const out: (number | '…')[] = [];
  for (let i = 0; i < sorted.length; i += 1) {
    const p = sorted[i];
    const prev = sorted[i - 1];
    if (prev != null && p - prev > 1) out.push('…');
    out.push(p);
  }
  return out;
}

const PaginationBar: React.FC<PaginationBarProps> = ({
  page,
  totalPages,
  onChange,
  hideIfSinglePage = false,
  rowsPerPage,
  rowsPerPageOptions = DEFAULT_ROWS_OPTIONS,
  onRowsPerPageChange,
}) => {
  const safeTotalPages = Math.max(1, totalPages);
  const safePage = Math.min(Math.max(1, page), safeTotalPages);

  const goToPage = useCallback(
    (nextPage: number) => {
      const clamped = Math.min(Math.max(1, nextPage), safeTotalPages);
      onChange(clamped);
    },
    [onChange, safeTotalPages],
  );

  const pages = useMemo(() => pageRange(safeTotalPages, safePage), [safeTotalPages, safePage]);

  const showRowsPerPage =
    rowsPerPage != null && onRowsPerPageChange != null && rowsPerPageOptions.length > 0;

  const handleRowsChange = (event: SelectChangeEvent<number>) => {
    const n = Number(event.target.value);
    if (!Number.isFinite(n) || onRowsPerPageChange == null) return;
    onRowsPerPageChange(n);
  };

  if (hideIfSinglePage && safeTotalPages <= 1) return null;

  const navGroup = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        flexWrap: 'wrap',
        ...(showRowsPerPage
          ? { ml: { xs: 0, sm: 'auto' } }
          : { width: '100%', justifyContent: 'space-between' }),
      }}
    >
      <Button
        variant="text"
        onClick={() => goToPage(safePage - 1)}
        disabled={safePage <= 1}
        startIcon={<ChevronLeftIcon />}
        sx={{ textTransform: 'none', color: colors.text.secondary }}
      >
        Previous
      </Button>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {pages.map((p, idx) =>
          p === '…' ? (
            <Typography key={`ellipsis-${idx}`} sx={{ color: colors.text.disabled, px: 1 }}>
              …
            </Typography>
          ) : (
            <IconButton
              key={p}
              onClick={() => goToPage(p)}
              disabled={safeTotalPages <= 1}
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                backgroundColor: p === safePage ? colors.primaryLight : 'transparent',
                color: p === safePage ? colors.text.light : colors.text.secondary,
                '&:hover': {
                  backgroundColor: p === safePage ? colors.sidebar.selected : colors.background.disabled,
                },
                '&.Mui-disabled': {
                  color: p === safePage ? colors.text.light : colors.text.secondary,
                  backgroundColor: p === safePage ? colors.primaryLight : 'transparent',
                  opacity: 1,
                },
              }}
              aria-label={`Go to page ${p}`}
            >
              <Typography sx={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium }}>
                {p}
              </Typography>
            </IconButton>
          ),
        )}
      </Box>

      <Button
        variant="text"
        onClick={() => goToPage(safePage + 1)}
        disabled={safePage >= safeTotalPages}
        endIcon={<ChevronRightIcon />}
        sx={{ textTransform: 'none', color: colors.text.secondary }}
      >
        Next
      </Button>
    </Box>
  );

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 2,
        border: `1px solid ${colors.border.light}`,
        backgroundColor: colors.background.card,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        flexWrap: 'wrap',
      }}
    >
      {showRowsPerPage && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography
            component="span"
            sx={{ color: colors.text.secondary, fontSize: typography.fontSize.sm, whiteSpace: 'nowrap' }}
          >
            Rows per page:
          </Typography>
          <FormControl size="small" variant="outlined">
            <Select<number>
              value={rowsPerPage}
              onChange={handleRowsChange}
              inputProps={{ 'aria-label': 'Rows per page' }}
              sx={{
                minWidth: 72,
                borderRadius: 2,
                fontSize: typography.fontSize.sm,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: colors.border.medium,
                },
              }}
            >
              {rowsPerPageOptions.map((n) => (
                <MenuItem key={n} value={n}>
                  {n}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}

      {showRowsPerPage ? navGroup : <Box sx={{ width: '100%' }}>{navGroup}</Box>}
    </Paper>
  );
};

export default PaginationBar;
